import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import { createOfertaAoVivo, updateOfertaAoVivo, analyzeProductOfferWithAi } from "../../services/api";
import ProductImageGalleryModal from "../../components/ProductImageGalleryModal";

import { CATEGORIAS, offerCategory } from "./offerPresentation";

function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export default function OfertaModal({ show, onHide, onSaved, oferta }) {
  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [price, setPrice] = useState("");
  const [cupom, setCupom] = useState("");
  const [linkResgate, setLinkResgate] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [showGallery, setShowGallery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) return;
    if (oferta) {
      setName(oferta.name || "");
      setLink(oferta.link || "");
      setPrice(oferta.offer_price != null ? Number(oferta.offer_price) : "");
      setCupom(oferta.cupom || "");
      setLinkResgate(oferta.link_resgate || "");
      setCategory(offerCategory(oferta));
      setDescription(oferta.description || "");
      setImage(oferta.image_url || "");
    } else {
      setName("");
      setLink("");
      setPrice("");
      setCupom("");
      setLinkResgate("");
      setCategory("");
      setDescription("");
      setImage("");
    }
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, oferta]);

  useEffect(() => {
    if (!show) return undefined;
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const reset = () => {
    setName("");
    setLink("");
    setPrice("");
    setCupom("");
    setLinkResgate("");
    setCategory("");
    setDescription("");
    setImage("");
    setError("");
  };

  const close = () => {
    reset();
    onHide();
  };

  const buscarIA = async () => {
    if (!link.trim()) {
      setError("Cole um link de produto.");
      return;
    }
    setSearching(true);
    setError("");
    try {
      const res = await analyzeProductOfferWithAi({
        link: link.trim(),
        product_name: name.trim(),
        platform: "",
      });
      const a = res?.analysis || {};
      if (a.name && !name) setName(a.name);
      if (a.description) setDescription(a.description);
      if (a.image_url) setImage(a.image_url);
      if (a.category_hint) {
        const hint = normalizeText(a.category_hint);
        const matched = CATEGORIAS.find(
          (c) => normalizeText(c).includes(hint) || hint.includes(normalizeText(c))
        );
        if (matched) setCategory(matched);
      }
    } catch (e) {
      setError("Não foi possível buscar com a IA.");
    } finally {
      setSearching(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImage(ev.target.result);
    reader.readAsDataURL(f);
  };

  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file" && item.type && item.type.indexOf("image") !== -1) {
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (ev) => setImage(ev.target.result);
          reader.readAsDataURL(file);
          e.preventDefault();
          return;
        }
      }
    }
    const text = e.clipboardData?.getData("text") || "";
    if (/^https?:\/\/\S+\.(png|jpe?g|webp|gif|avif)(\?\S*)?$/i.test(text.trim())) {
      setImage(text.trim());
      e.preventDefault();
    }
  };

  const onSelectGallery = (img) => {
    if (!img) return;
    setImage(img.url || img.thumbnail_url || "");
    setShowGallery(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Preencha o título.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        link: link.trim(),
        offer_price: price === "" || price == null ? null : Number(price),
        cupom: cupom.trim(),
        link_resgate: linkResgate.trim(),
        category: category || null,
      };
      if (!oferta || image !== (oferta.image_url || "")) {
        payload.image = image || null;
      }
      const result = oferta
        ? await updateOfertaAoVivo(oferta.id, payload)
        : await createOfertaAoVivo(payload);
      reset();
      onSaved(result);
    } catch (err) {
      setError(err?.response?.data?.error || "Falha ao salvar a oferta.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={close} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{oferta ? "Editar Oferta" : "Adicionar Oferta"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={submit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Link do produto (opcional)</Form.Label>
              <div className="d-flex" style={{ gap: 10 }}>
                <Form.Control
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="Cole o link do produto..."
                />
                <Button
                  type="button"
                  variant="primary"
                  onClick={buscarIA}
                  disabled={searching}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {searching ? <Spinner size="sm" animation="border" /> : "Buscar"}
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Título</Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Smart TV LED 55 4K Samsung"
              />
            </Form.Group>

            <div className="row">
              <div className="col-12 col-sm-6">
                <Form.Group className="mb-3">
                  <Form.Label>Preço (R$)</Form.Label>
                  <NumericFormat
                    value={price}
                    customInput={Form.Control}
                    thousandSeparator="."
                    decimalSeparator=","
                    allowNegative={false}
                    decimalScale={2}
                    fixedDecimalScale={false}
                    prefix="R$ "
                    onValueChange={({ floatValue }) => setPrice(floatValue ?? "")}
                    placeholder="Ex: R$ 2.310,00"
                  />
                </Form.Group>
              </div>
              <div className="col-12 col-sm-6">
                <Form.Group className="mb-3">
                  <Form.Label>Cupom</Form.Label>
                  <Form.Control
                    value={cupom}
                    onChange={(e) => setCupom(e.target.value.toUpperCase())}
                    placeholder="CEL15"
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Link de resgate de cupom (opcional)</Form.Label>
              <Form.Control
                value={linkResgate}
                onChange={(e) => setLinkResgate(e.target.value)}
                placeholder="https://shopee.com.br/m/cupons..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Categoria</Form.Label>
              <Form.Control
                as="select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Selecione...</option>
                {CATEGORIAS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group>
              <Form.Label>Imagem</Form.Label>
              <div className="d-flex align-items-center" style={{ gap: 16 }}>
                <div className="oav-thumb">
                  {image ? <img src={image} alt="" /> : <span>🖼️</span>}
                </div>
                <div className="d-flex flex-column" style={{ gap: 8 }}>
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    onClick={() => document.getElementById("oav-file").click()}
                  >
                    Upload
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    onClick={() => setShowGallery(true)}
                  >
                    Galeria
                  </Button>
                  <input
                    id="oav-file"
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={onFile}
                  />
                </div>
              </div>
              <Form.Text className="text-muted">
                Dica: copie uma imagem e cole aqui com <strong>Ctrl+V</strong>.
              </Form.Text>
            </Form.Group>

            {error && <div className="text-danger mt-3">{error}</div>}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={close}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? "Salvando..." : oferta ? "Salvar" : "Adicionar Oferta"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ProductImageGalleryModal
        show={showGallery}
        onHide={() => setShowGallery(false)}
        onSelect={onSelectGallery}
      />
    </>
  );
}

